import {useQuery} from "@apollo/client";
import {DateRange} from "react-day-picker";
import {useFormContext} from "react-hook-form";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {DatePickerField} from "@/components/form/fields/DatePickerField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {timezoneAbbr} from "@/lib/format.tsx";
import {GQL_STATUS_PAGE_QUERY, GQL_STATUS_PAGES_NAMES_QUERY} from "@/lib/queries/statuspages.ts";
import {IncidentType, StatusPageList, StatusPageResponse, UptimeCalculationType} from "@/lib/types/statuspages.ts";
import {AffectedComponentsField} from "@/pages/incidents/components/incident-edit/components/AffectedComponentsField.tsx";
import {IncidentEditFormData} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";

export const GlobalSettingsForm = ({disableStatusPageSelect}: {disableStatusPageSelect?: boolean}) => {
  const {setValue, watch, resetField} = useFormContext<IncidentEditFormData>();

  const incidentId = watch("id");
  const statuspageId = watch("statuspage_id");
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;
  const incidentType = watch("incident_type");
  const {data: statusPagesData} = useQuery<StatusPageList>(GQL_STATUS_PAGES_NAMES_QUERY);
  const statusPageOptions = statusPagesData?.statusPages?.items.map((sp) => ({value: sp.id, label: sp.name}));
  const {data: statusPageData} = useQuery<StatusPageResponse>(GQL_STATUS_PAGE_QUERY, {
    variables: {id: statuspageId},
    skip: !statuspageId,
    errorPolicy: "ignore",
    fetchPolicy: "cache-first",
  });
  // check-based pages derive downtime from their checks, so the flag has no effect there
  const isCheckBased = statusPageData?.statusPage?.uptime_calculation_type === UptimeCalculationType.BY_CHECKS;
  const supportLink =
    incidentType === IncidentType.INCIDENT
      ? "https://support.uptime.com/hc/en-us/articles/23510009217052-Status-Page-Incidents"
      : "https://support.uptime.com/hc/en-us/articles/24296553040412-Status-Page-Maintenance";
  const supportLinkTitle =
    incidentType === IncidentType.INCIDENT
      ? "Create an Incident and issue notifications to alert your subscribers of outages."
      : "Schedule maintenance windows and issue notifications to alert your subscribers of planned downtime events.";
  return (
    <div className={"d-flex flex-column gap-4"}>
      <div className="pulseTextBody">
        {supportLinkTitle}{" "}
        <a target="_blank" href={supportLink}>
          View Documentation
        </a>
      </div>
      <InputField name={"name"} label={"Title"} required />
      <div className={"d-flex flex-column gap-3"}>
        <div className="row">
          <div className="col">
            <DatePickerField
              name={"starts_at"}
              label={"Starts"}
              helpText={`Times are in ${timezoneAbbr(timezone)}.`}
              mode={"split"}
              datePickerV3Props={{
                splitRole: "start",
                placement: "bottom-start",
                selected: {from: watch("starts_at"), to: watch("ends_at") ?? undefined},
                onChange: (date) => {
                  const dateRange = date as DateRange;
                  if (dateRange) {
                    setValue("starts_at", dateRange.from, {shouldDirty: true, shouldValidate: true});
                  } else {
                    resetField("starts_at");
                  }
                },
              }}
            />
          </div>
          <div className="col">
            <DatePickerField
              name={"ends_at"}
              label={"Ends"}
              helpText={`Times are in ${timezoneAbbr(timezone)}.`}
              placeholder={"ongoing"}
              mode={"split"}
              datePickerV3Props={{
                splitRole: "end",
                placement: "bottom-end",
                selected: {from: watch("starts_at"), to: watch("ends_at") ?? undefined},
                onChange: (date) => {
                  const dateRange = date as DateRange;
                  setValue("ends_at", dateRange ? dateRange.to : undefined, {shouldDirty: true, shouldValidate: true});
                },
              }}
            />
          </div>
        </div>
        {!isCheckBased && (
          <div className="mb-2">
            <SwitchField
              name={"include_in_global_metrics"}
              label={"This Incident’s duration will contribute to global downtime calculations."}
            />
          </div>
        )}
        {incidentType === IncidentType.SCHEDULED_MAINTENANCE && (
          <div>
            <SwitchField
              name={"send_maintenance_start_notification"}
              label={"Send automated notification to subscribers when this Scheduled Maintenance starts"}
            />
          </div>
        )}
      </div>
      <Divider />
      <div>
        <SelectField
          name={"statuspage_id"}
          isSearchable={true}
          isClearable={false}
          disabled={!!incidentId || disableStatusPageSelect}
          label={"Select Status Page"}
          tooltip={"Incident will be published to this page."}
          helpText={disableStatusPageSelect ? "" : "Incident will be published to this page."}
          required={true}
          options={statusPageOptions}
        />
      </div>
      {!!statuspageId && <AffectedComponentsField statusPageId={statuspageId} />}
    </div>
  );
};
